import React, { useState } from 'react';
import { DataTitle, DataValue, NoData, PageCard, Poppins, Settings, Spacer, TitleWithBack } from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import styled from 'styled-components';
import CommonMenu from 'src/components/CommonMenu';
import { formatDate, getCurrencySymbol } from 'src/utils/misc';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import useVersion from 'src/hooks/useVersion';
import { VersionHeader } from 'src/hooks/useVersion/comps';
import { formatUrl } from 'src/utils/misc';
import { OpenInNew } from '@mui/icons-material';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { useStateSelector } from 'src/redux';
import { formatNumber } from 'src/utils/misc';
import { ControlQuery, ObjectKind } from 'src/graphql/_generated';
import { maturityLevelNames, QKeys } from 'src/api/types';
import QuickActionsMenu from 'src/components/QuickActionsMenu';
import { queryCache } from 'react-query';
import InfoHint from 'src/components/InfoHint';
import { LABELS } from 'src/components/labels';
import Comments from 'src/components/Comments';

const WhiteBox = styled(PageCard)`
  padding-bottom: 0;
  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__title {
      font-weight: 600;
      line-height: 48px;
      color: ${({ theme }) => theme.colors.prussianBlue};
    }

    &__actions {
      display: flex;
      align-items: center;
      grid-gap: 20px;
    }
  }

  .grid-view {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-gap: 50px;
    max-width: 1100px;

    &--3-2 {
      grid-template-columns: 1fr 1fr 3fr;
      padding-right: 100px;
    }
  }
  .panel {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-gap: 15px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .grid-view {
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px;

      &--1-3 {
        grid-template-columns: 1fr;
        padding-right: 100px;
      }
    }
  }
`;

type ModalId = 'comments';

interface MetaSheetProps {
  data: NonNullable<ControlQuery['control']>;
  editable?: { onEditSelect: () => void; onDeleteSelect: () => void };
  children?: React.ReactNode;
  commentsPanel?: { isOpen: boolean; onToggle: (open: boolean) => void };
}

export const MetaSheet: React.FC<MetaSheetProps> = ({ data, editable, children, commentsPanel }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);
  const [modalId, setModalId] = useState<ModalId | null>(null);
  const { versionId, version } = useVersion();

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      {!commentsPanel && modalId === 'comments' && (
        <Comments
          onClose={() => setModalId(null)}
          eventData={{ objectType: ObjectKind.Control, objectId: data.id }}
          refetch={() => queryCache.invalidateQueries([QKeys.Audit])}
        />
      )}

      <WhiteBox>
        <div className="head">
          <TitleWithBack title="CONTROL" gradient />
          {editable && !versionId && (
            <div className="head__actions">
              <QuickActionsMenu
                actions={[
                  {
                    type: 'EVENT_ACTION',
                    disabled: !!versionId,
                    onClick: () => {
                      if (commentsPanel) {
                        commentsPanel.onToggle(!commentsPanel.isOpen);
                      } else {
                        setModalId('comments');
                      }
                      mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Comments', tags: ['CONTROL'] });
                    },
                  },
                ]}
              />
              <Settings
                active={!!anchorEl}
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
                data-cy="common-menu"
              />
              <CommonMenu
                onEditClick={(e) => onMenuClick(e, () => editable.onEditSelect())}
                onDeleteClick={(e) => onMenuClick(e, () => editable.onDeleteSelect())}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              />
            </div>
          )}
        </div>
        <Spacer $px={30} />
        <Poppins className="head__title" px={32} data-cy="title">
          {data.name}
        </Poppins>
        {version && (
          <>
            <Spacer $px={5} />
            <VersionHeader version={version} />
          </>
        )}
        <Spacer $px={44} />
        <div className="grid-view">
          <div>
            <DataTitle>
              <InfoHint tooltipText={LABELS.controlStrategy}>STRATEGY</InfoHint>
            </DataTitle>

            {data.strategy ? <DataValue>{data.strategy}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>OWNER</DataTitle>
            {data.owner?.text ? <DataValue>{data.owner.text}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>
              <InfoHint tooltipText={LABELS.controlStatus}>STATUS</InfoHint>
            </DataTitle>
            {data.status ? <DataValue>{data.status}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>LAST EDITED</DataTitle>
            {data.meta?.updatedAt ? (
              <DataValue>
                <TLLink date={data.meta.updatedAt}>
                  {formatDate(data.meta.updatedAt, { formatType: 'datetime' })}
                </TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <Spacer $px={40} />
        <div className="grid-view">
          <div>
            <DataTitle>EXISTING MATURITY</DataTitle>
            {data.existingMaturity ? <DataValue>{maturityLevelNames[data.existingMaturity]}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>TARGET MATURITY</DataTitle>
            {data.targetMaturity ? <DataValue>{maturityLevelNames[data.targetMaturity]}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>EXPECTED LIVE DATE</DataTitle>
            {data.expectedActiveDate ? (
              <DataValue>
                <TLLink date={data.expectedActiveDate}>{formatDate(data.expectedActiveDate)}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>ACTUAL LIVE DATE</DataTitle>
            {data.actualActiveDate ? (
              <DataValue>
                <TLLink date={data.actualActiveDate}>{formatDate(data.actualActiveDate)}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>REVIEW DATE</DataTitle>
            {data.reviewDate ? (
              <DataValue>
                <TLLink date={data.reviewDate}>{formatDate(data.reviewDate)}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <Spacer $px={40} />
        <div className="grid-view">
          <div>
            <DataTitle>ANNUAL COST</DataTitle>
            {data.annualCost ? (
              <DataValue>
                {getCurrencySymbol()}
                {formatNumber(data.annualCost)}
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>VENDOR</DataTitle>
            {data.vendor ? <DataValue>{data.vendor}</DataValue> : <NoData />}
          </div>
          <div>
            <DataTitle>LINK</DataTitle>
            {data.linkUrl ? (
              <DataValue>
                <a
                  href={formatUrl(data.linkUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="table-link"
                  onClick={() =>
                    mpEvent(MPEvents.Link, {
                      source: { value: ['Control'], params: { id: data.id } },
                      destination: { value: ['External URL'] },
                      tags: ['CONTROL'],
                    })
                  }
                >
                  {data.linkText || data.linkUrl}{' '}
                  <OpenInNew
                    css={`
                      font-size: 14px !important;
                      color: ${colors.prussianBlue};
                    `}
                  />
                </a>
              </DataValue>
            ) : data.linkText ? (
              <DataValue>{data.linkText}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <Spacer $px={40} />
        <div>
          <DataTitle>DESCRIPTION</DataTitle>
          {data.description ? <ExpandCollapse text={data.description} /> : <NoData />}
        </div>
        {children}
      </WhiteBox>
    </>
  );
};
